import { useRegisterSW } from 'virtual:pwa-register/react';
import { useEffect, useRef, useCallback, createElement } from 'react';
import { toast } from 'sonner';
import { ArrowUpCircle } from 'lucide-react';

// Detect iOS devices (Safari has unique SW behavior)
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
};

// Check interval: 5 minutes for regular devices, 2 minutes for iOS (more aggressive due to SW eviction)
const UPDATE_CHECK_INTERVAL = isIOS() ? 2 * 60 * 1000 : 5 * 60 * 1000;

export function ReloadPrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);
  const hasCheckedOnLaunch = useRef(false);
  const updatePendingRef = useRef(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      registrationRef.current = registration;
      console.log('[SW] Registered:', swUrl);

      if (registration) {
        // Immediate update check on launch (first registration)
        if (!hasCheckedOnLaunch.current) {
          hasCheckedOnLaunch.current = true;
          console.log('[SW] Checking for updates on launch...');
          registration.update().catch((err) => {
            console.warn('[SW] Update check on launch failed:', err);
          });
        }

        // Periodic update checks
        setInterval(() => {
          console.log('[SW] Periodic update check...');
          registration.update().catch((err) => {
            console.warn('[SW] Periodic update check failed:', err);
          });
        }, UPDATE_CHECK_INTERVAL);
      }
    },
    onRegisterError(error: Error) {
      console.error('[SW] Registration error:', error);
    },
  });

  // When update is available, auto-apply and show a toast after reload
  useEffect(() => {
    if (needRefresh) {
      console.log('[SW] New version available, applying update...');
      updatePendingRef.current = true;
      updateServiceWorker(true);
      // Fallback: if controllerchange doesn't fire within 5s, force reload
      setTimeout(() => window.location.reload(), 5000);
    }
  }, [needRefresh, updateServiceWorker]);

  // Listen for the new SW taking control — reload and show toast on next load
  useEffect(() => {
    const handleControllerChange = () => {
      if (!updatePendingRef.current) return;
      console.log('[SW] Controller changed — new SW active, reloading...');
      sessionStorage.setItem('app_just_updated', '1');
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  // Show toast after reload if app was just updated
  useEffect(() => {
    if (sessionStorage.getItem('app_just_updated') === '1') {
      sessionStorage.removeItem('app_just_updated');
      toast('Your application has been updated', {
        icon: createElement('div', { className: 'w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0' },
          createElement(ArrowUpCircle, { className: 'h-4 w-4 text-white' })
        ),
      });
    }
  }, []);

  // iOS-specific: Re-verify service worker is still controlling the page on visibility change
  const checkForUpdates = useCallback(() => {
    const registration = registrationRef.current;
    if (registration) {
      console.log('[SW] Manual/event-triggered update check...');
      registration.update().catch((err) => {
        console.warn('[SW] Update check failed:', err);
      });
    }
  }, []);

  // Visibility change handler - check for updates when app becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[SW] App became visible, checking for updates...');
        checkForUpdates();

        // iOS: Re-verify service worker is still controlling the page
        if (isIOS() && navigator.serviceWorker.controller === null) {
          console.log('[SW] iOS: No controller, re-registering...');
          navigator.serviceWorker.ready.then((reg) => {
            if (!reg.active) {
              window.location.reload();
            }
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdates]);

  // Focus handler - check for updates when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      console.log('[SW] Window focused, checking for updates...');
      checkForUpdates();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkForUpdates]);

  // Network reconnect handler - check for updates when coming back online
  useEffect(() => {
    const handleOnline = () => {
      console.log('[SW] Network restored, checking for updates...');
      setTimeout(checkForUpdates, 1000);
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [checkForUpdates]);

  // Expose checkForUpdates globally for manual trigger from settings
  useEffect(() => {
    (window as unknown as { __checkForPWAUpdates?: () => Promise<boolean> }).__checkForPWAUpdates = async () => {
      const registration = registrationRef.current;
      if (registration) {
        console.log('[SW] Manual update check triggered from settings...');
        try {
          await registration.update();
          return needRefresh || registration.waiting !== null;
        } catch (err) {
          console.warn('[SW] Manual update check failed:', err);
          return false;
        }
      }
      return false;
    };

    return () => {
      delete (window as unknown as { __checkForPWAUpdates?: () => Promise<boolean> }).__checkForPWAUpdates;
    };
  }, [needRefresh]);

  return null;
}

// Export for use in other components
export { isIOS };
